import type { generated } from '@tempo-io/item-store-dto';
import type { z } from 'zod';
import type { AlwaysPresent, Optional } from './generatedShape';

export const DependencyType = {
  Successor: 'SUCCESSOR',
  Predecessor: 'PREDECESSOR',
} as const;

export type DependencyType = ItemDependency['dependencyType'];

export const SchedulingType = {
  ACTUALS_ONLY: 'ACTUALS_ONLY',
  PLANNED_AND_ACTUALS: 'PLANNED_AND_ACTUALS',
} as const;

export type SchedulingType = ItemResponse['schedulingType'];

export type ItemDependency = z.infer<typeof generated.itemDependencySchema>;

export type ItemDescendantsDetails = z.infer<typeof generated.itemDescendantsDetailsSchema>;

type ItemResponse = z.infer<typeof generated.itemResponseSchema>;

// Three deliberate departures from the generated schema:
// - customFields is `catchall(any)` there; the key-tagged union below is strictly more precise.
// - dependencies is nullish there; the API omits the key entirely when not requested, and every
//   consumer treats it as a list.
// - planId is an adaptive-planner concern with no item-store counterpart.
export type Item = Optional<
  AlwaysPresent<
    Omit<ItemResponse, 'customFields' | 'dependencies'>,
    | 'description'
    | 'parentId'
    | 'teamId'
    | 'timeSpentSeconds'
    | 'earliestStartDateTime'
    | 'deadlineDateTime'
    | 'plannedStartDateTime'
    | 'plannedFinishDateTime'
    | 'doneDateTime'
  >,
  'parentManagedByIntegration'
> & {
  customFields: ItemCustomFieldValues;
  dependencies: ItemDependency[];
  planId?: number;
};

export type CustomFieldValueDto = {
  value: string | number | boolean | null;
  description?: string;
};

export type CustomFieldValuesEntry = CustomFieldValueDto | CustomFieldValueDto[] | null | undefined;

export const isCustomFieldValueDto = (v: unknown): v is CustomFieldValueDto =>
  typeof v === 'object' && v !== null && !Array.isArray(v) && 'value' in v;

export const unwrapSingleDto = (entry: CustomFieldValuesEntry): CustomFieldValueDto['value'] =>
  entry == null || Array.isArray(entry) ? null : entry.value;

export const unwrapMultiDto = (entry: CustomFieldValuesEntry): CustomFieldValueDto['value'][] =>
  Array.isArray(entry) ? entry.map((dto) => dto.value) : [];

export const wrapAsDtos = <T extends string | number | boolean>(
  values: T[],
): CustomFieldValueDto[] => values.map((value) => ({ value }));

export type ItemV2 = Omit<Item, 'customFields'> & {
  customFieldValues?: {
    [key: string]: CustomFieldValuesEntry;
  };
};

export type ItemCustomFieldValues = {
  [key: string]: ItemCustomFieldValue;
};

export type ItemPicklistValue = { PICKLIST: number };
export type ItemMultiPicklistValue = { id: number; MULTI_PICKLIST: number };
export type ItemMultiCustomerValue = { id: number; MULTI_CUSTOMER: number };
export type ItemItemValue = { ITEM: number };
export type ItemMultiItemValue = { id: number; MULTI_ITEM: number };
export type ItemUserValue = { USER: string };
export type ItemMultiUserValue = { id: number; MULTI_USER: string };
export type ItemLinkValue = { LINK: string; description?: string };
export type ItemMultiLinkValue = { id: number; MULTI_LINK: string; description?: string };
export type ItemTextValue = { TEXT: string };
export type ItemMultiLineTextValue = { MULTILINE_TEXT: string };
export type ItemNoteValue = { NOTE: string };
export type ItemNumericValue = { NUMERIC: number };
export type ItemCurrencyValue = { CURRENCY: number };
export type ItemMultiTextValue = { id: number; MULTI_TEXT: string };
export type ItemCheckboxValue = { CHECKBOX: boolean };
export type ItemDateValue = { DATE: string };

export type ItemCustomFieldValue =
  | ItemPicklistValue
  | ItemMultiPicklistValue[]
  | ItemMultiCustomerValue[]
  | ItemItemValue
  | ItemMultiItemValue[]
  | ItemUserValue
  | ItemMultiUserValue
  | ItemTextValue
  | ItemMultiLineTextValue
  | ItemNoteValue
  | ItemLinkValue
  | ItemMultiLinkValue[]
  | ItemNumericValue
  | ItemMultiTextValue[]
  | ItemMultiUserValue[]
  | ItemCurrencyValue
  | ItemCheckboxValue
  | ItemDateValue;

export type ItemDataSet = Record<number, DataSetItem>;

interface DataSetItem {
  parentId: number | null;
  level: number;
}

export interface ItemTreeSetEntry {
  id: number;
  parentId: number | null;
  matched: boolean;
  itemTypeId: number;
  hasExternalKey: boolean;
  loopId?: number;
  parentManagedByIntegration: boolean;
  isDeletedInItemStore?: boolean;
}

export interface ItemTreeSet {
  recordCount: number | null;
  data: ItemTreeSetEntry[];
}

// `name` is only populated on some ancestor lookups.
export type ItemAncestor = Optional<z.infer<typeof generated.itemAncestorSchema>, 'name'>;

export type BatchItemAncestorsRequest = z.infer<typeof generated.batchItemAncestorsRequestSchema>;

export type PaginatedItems = Omit<
  z.infer<typeof generated.paginationDocumentItemResponseSchema>,
  'data' | 'cursor'
> & { data: readonly Item[]; cursor?: string };

export type ItemRequestModelField = 'DEPENDENCIES' | 'CUSTOM_FIELDS' | 'INTEGRATIONS';

export type ItemFieldDto = z.infer<typeof generated.itemFieldDtoSchema>;

// planId has no item-store counterpart; relativePriority narrows the generated flat object (see below).
export type ItemPatchRequest = Omit<
  z.infer<typeof generated.patchItemRequestSchema>,
  'relativePriority'
> & {
  planId?: number | null;
  relativePriority?: ItemRelativePriority;
};

export type CreateItemRequest = Omit<
  z.infer<typeof generated.createItemRequestSchema>,
  'relativePriority'
> & { relativePriority?: ItemRelativePriority };

// Narrower than the generated `{ id: int | null, type }`, which cannot express that `id` is required
// for before/after and absent for first.
type ItemRelativePriority = { id: number; type: 'before' | 'after' } | { type: 'first' };

// generated.projectedItemResponseSchema now describes this projection properly, but adopting it would
// change every consumer signature. Kept as Partial<ItemV2> here; switching over is its own change.
export type BulkFetchV2Response = { itemsById: Record<string, Partial<ItemV2>> };

export type TypedFieldValue = z.infer<typeof generated.typedFieldValueObjectSchema>;

export type CustomFieldPatch = z.infer<typeof generated.customFieldPatchSchema>;

export type BulkPartialPatchDependencyRequest = z.infer<
  typeof generated.bulkPartialPatchDependencyRequestSchema
>;

// fields carries the local ItemPatchRequest so planId and the narrowed relativePriority survive.
export type BulkPartialPatchRequest = Omit<
  z.infer<typeof generated.bulkPartialPatchRequestSchema>,
  'fields'
> & { fields?: ItemPatchRequest | null };

// reason stays open so a new backend value does not break the toast; the generated enum is closed.
type BulkPatchSkipReason = 'NOT_APPLICABLE_TO_ITEM_TYPE' | (string & {});

type BulkPatchItemFieldSkip = Omit<
  z.infer<typeof generated.bulkPatchItemFieldSkipSchema>,
  'reason'
> & { reason: BulkPatchSkipReason };

export type BulkPartialPatchResponse = Omit<
  z.infer<typeof generated.bulkPartialPatchResponseSchema>,
  'skipped'
> & { skipped: BulkPatchItemFieldSkip[] };
